import { assert } from 'console'
import * as React from 'react'
import { ReactNode } from 'react'
import { ColorName } from '../constants'
import { TestIds } from '../data/testids'
import memoize from '../memoize'
import Checkbox from './Checkbox'
import Table, { TableColFn } from './Table'

interface TMultiselProps<R> {
  rows: R[]
  columns: TableColFn<R>[]
  headers?: ReactNode[]
  widths?: (string | null)[]
  alignments?: (string | null)[]
  keyFn?: (arg: R) => string | number
  checkColor?: ColorName
  onChange?: (keys: Set<string | number>) => void
  dataRowComponent?: React.ComponentType<any>
  dataCellComponent?: React.ComponentType<any>
  headerRowComponent?: React.ComponentType<any>
  headerCellComponent?: React.ComponentType<any>
}

interface TMultiselState {
  selectedKeys: Set<string | number>
}

/**
 * See Table.
 *
 * Provides multiselect via a row of checkboxes on left.
 *
 * An additional checkbox in the header allows select all / deselect all.
 */
export default class TableMultiselect<R> extends React.PureComponent<TMultiselProps<R>, TMultiselState> {
  private handlers = {}
  state: TMultiselState = { selectedKeys: new Set() }

  static getDerivedStateFromProps<R>(props: TMultiselProps<R>, state: TMultiselState) {
    const getRKey = (r: R, index: number): string | number => {
      if (props.keyFn == null) {
        return index
      }
      return props.keyFn(r)
    }

    const rowKeys = new Set<string | number>(props.rows.map(getRKey))

    const selectedKeys = new Set(state.selectedKeys)
    for (const k of Array.from(state.selectedKeys)) {
      if (!rowKeys.has(k)) {
        selectedKeys.delete(k)
      }
    }
    if (selectedKeys.size < state.selectedKeys.size) {
      return { selectedKeys }
    }
    return null
  }

  render() {
    const { rows, columns, headers, widths, alignments, keyFn } = this.props

    const { selectedKeys } = this.state
    const toggleAllCheck =
      selectedKeys.size === 0 ? 'unchecked' : selectedKeys.size === rows.length ? 'checked' : 'indeterminate'

    const cols = ([this.checkBoxCol] as TableColFn<R>[]).concat(columns)

    let heads = [
      <Checkbox
        key="toggle-sel-all"
        color={this.checkColor()}
        check={toggleAllCheck}
        onClick={this.toggleSelectAll}
        data-testid={TestIds.tableMultiselectCheckbox}
      />,
    ] as ReactNode[]
    heads = heads.concat(headers || [])

    let ws = ['24px'] as (string | null)[]
    ws = ws.concat(widths || [])

    const aligns = alignments == null ? undefined : (['left'] as (string | null)[]).concat(alignments)

    const { headerCellComponent, headerRowComponent, dataCellComponent, dataRowComponent } = this.props
    const components = { headerCellComponent, headerRowComponent, dataCellComponent, dataRowComponent }

    return (
      <Table rows={rows} columns={cols} headers={heads} alignments={aligns} widths={ws} keyFn={keyFn} {...components} />
    )
  }

  selectedKeys() {
    return this.state.selectedKeys
  }

  selectedRows(): R[] {
    const { rows } = this.props
    const { selectedKeys } = this.state
    return rows.filter((r, index) => selectedKeys.has(this.getRowKey(r, index)))
  }

  private checkColor(): ColorName {
    return this.props.checkColor || 'midway'
  }

  private checkBoxCol = (r: R, index: number) => {
    const { selectedKeys } = this.state
    const rowKey = this.getRowKey(r, index)
    return (
      <Checkbox
        color={this.checkColor()}
        check={selectedKeys.has(rowKey) ? 'checked' : 'unchecked'}
        onClick={memoize(this.handlers, rowKey, () => (e: MouseEvent) => this.toggleSelectKey(e, rowKey))}
      />
    )
  }

  private toggleSelectKey = (e: MouseEvent, clickedKey: string | number) => {
    const selectedKeys = new Set(this.state.selectedKeys)

    if (e.shiftKey && this.state.selectedKeys.size === 1) {
      // Shift select a block of rows
      this.blockSelect(clickedKey, selectedKeys)
    } else {
      // Select or deselect single row
      if (this.state.selectedKeys.has(clickedKey)) {
        selectedKeys.delete(clickedKey)
      } else {
        selectedKeys.add(clickedKey)
      }
    }

    this.setState({ selectedKeys }, this.maybeOnChange)
  }

  private toggleSelectAll = () => {
    let selectedKeys: Set<number | string>
    if (this.state.selectedKeys.size === 0) {
      // Select all
      selectedKeys = new Set(this.props.rows.map(this.getRowKey))
    } else {
      selectedKeys = new Set()
    }
    this.setState({ selectedKeys }, this.maybeOnChange)
  }

  private maybeOnChange = () => {
    if (this.props.onChange) {
      this.props.onChange(this.state.selectedKeys)
    }
  }

  private getRowKey = (r: R, index: number) => {
    if (this.props.keyFn == null) {
      return index
    }
    return this.props.keyFn(r)
  }

  /**
   * Block selects everything between the single currently selected row and the row just clicked.
   */
  private blockSelect(clickedKey: string | number, selectedKeys: Set<string | number>) {
    assert(selectedKeys.size === 1, 'Block select expects exactly one selected row')
    const rowKeys = this.props.rows.map(this.getRowKey)
    let inBlock = false
    for (const rowKey of rowKeys) {
      if (this.state.selectedKeys.has(rowKey)) {
        inBlock = !inBlock
      }
      if (clickedKey === rowKey) {
        inBlock = !inBlock
      }
      if (inBlock) {
        selectedKeys.add(rowKey)
      }
    }
    selectedKeys.add(clickedKey)
  }
}
